import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  EyeIcon,
  BanknotesIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { useAuth } from '../../auth/firebaseAuth';

const SafetyPage: React.FC = () => {
  const analytics = useAnalyticsWithConsent();
  const { currentUser } = useAuth();

  analytics.trackPixelPageView({
    email: currentUser?.email || undefined,
    externalId: currentUser?.uid,
  });
  const commonScams = [
    {
      title: 'Advance Payment Scams',
      description: 'Scammer asks for deposit/rent before showing the property or signing a lease.',
      icon: BanknotesIcon,
      signs: [
        'Pressure to send money quickly',
        'Requests for wire transfers or cryptocurrency',
        'Unusually low prices for the area',
        'Unable to view the property in person',
      ],
    },
    {
      title: 'Identity Theft Attempts',
      description: 'Requests for unnecessary personal information or documentation before viewing.',
      icon: UserGroupIcon,
      signs: [
        'Asking for PPS number before viewing',
        'Requesting copies of passport/ID without meeting',
        'Excessive personal information requests',
        'Pressuring for immediate document sharing',
      ],
    },
    {
      title: 'Fake Listings',
      description: 'Copy legitimate listings with altered contact details or prices.',
      icon: DocumentTextIcon,
      signs: [
        'Images found on multiple listings',
        'Price significantly below market value',
        'Poor quality or stock photos',
        "Listing details don't match location",
      ],
    },
    {
      title: 'Overseas Landlord Scams',
      description: 'Claims to be abroad and cannot show property but requests payment.',
      icon: EyeIcon,
      signs: [
        'Cannot meet in person',
        'Overseas payment requests',
        'Complicated story about being abroad',
        'Offers to mail keys after payment',
      ],
    },
  ];

  const safetyTips = [
    'Always view a property in person before making any payments',
    'Meet the landlord or agent at the property',
    'Never wire money or pay in cryptocurrency',
    'Get a proper lease agreement and receipt for any payments',
    'Be wary of prices that seem too good to be true',
    'Use official RentSmart.ie messaging system for all communications',
    'Report suspicious listings immediately',
  ];

  // SEO Helmet block
  const seoTitle = 'RentSmart Safety Center | Rental Scam Prevention & Safety Tips';
  const seoDescription =
    'Learn how to stay safe on RentSmart.ie. Discover common rental scams, safety tips, and how to report suspicious listings. Protect yourself and your rental experience in Ireland.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="rental safety, scam prevention, RentSmart safety, report listing, rental tips, Ireland, safe renting, suspicious listings"
        />
        <link rel="canonical" href="https://rentsmart.ie/safety" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/safety" />
      </Helmet>
      <div className="min-h-screen py-6 sm:py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alert Banner */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 mb-6 sm:mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-400" />
              </div>
              <div className="ml-2.5 sm:ml-3">
                <h3 className="text-xs sm:text-sm font-medium text-yellow-800">
                  Stay Safe on RentSmart.ie
                </h3>
                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-yellow-700">
                  Your safety is our priority. Please read these guidelines carefully and report any
                  suspicious activity.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8 sm:space-y-10 md:space-y-12">
            {/* Header */}
            <div className="text-center">
              <ShieldExclamationIcon className="mx-auto h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-indigo-600" />
              <h1 className="mt-3 sm:mt-4 text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">
                RentSmart Safety Center
              </h1>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-500 max-w-2xl mx-auto px-4">
                Protect yourself from rental scams and stay safe while using RentSmart.ie. Learn
                about common scams and best practices.
              </p>
            </div>

            {/* Common Scams Section */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6 md:mb-8">
                Common Rental Scams to Watch Out For
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 md:gap-8">
                {commonScams.map((scam) => (
                  <div
                    key={scam.title}
                    className="bg-white rounded-lg shadow-lg p-4 sm:p-5 md:p-6 hover:shadow-xl transition-shadow"
                  >
                    <div className="flex items-center mb-3 sm:mb-4">
                      <scam.icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-indigo-600 flex-shrink-0" />
                      <h3 className="ml-2.5 sm:ml-3 text-base sm:text-lg md:text-xl font-semibold text-gray-900">
                        {scam.title}
                      </h3>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                      {scam.description}
                    </p>
                    <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-2">
                      Warning Signs:
                    </h4>
                    <ul className="list-disc pl-4 sm:pl-5 text-xs sm:text-sm md:text-base text-gray-600 space-y-1">
                      {scam.signs.map((sign, index) => (
                        <li key={index}>{sign}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety Tips Section */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6 md:mb-8">
                Safety Tips
              </h2>
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-5 md:p-6">
                <div className="grid gap-3 sm:gap-4">
                  {safetyTips.map((tip, index) => (
                    <div key={index} className="flex items-start">
                      <ClipboardDocumentCheckIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2.5 sm:mr-3 flex-shrink-0 mt-0.5" />
                      <p className="text-sm sm:text-base text-gray-700">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Report Section */}
            <div className="bg-indigo-50 rounded-lg p-5 sm:p-6 md:p-8 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-indigo-900 mb-3 sm:mb-4">
                Need to Report Something?
              </h2>
              <p className="text-sm sm:text-base text-indigo-700 mb-5 sm:mb-6 px-4">
                If you encounter a suspicious listing or message, please report it immediately. Our
                team will investigate and take appropriate action.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
                <Link
                  to="/report-listing"
                  className="inline-flex items-center w-full sm:w-auto justify-center px-5 py-2.5 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 no-underline"
                >
                  Report a Listing
                </Link>
                <Link
                  to="/help-center"
                  className="inline-flex items-center w-full sm:w-auto justify-center px-5 py-2.5 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 no-underline"
                >
                  Visit Help Center
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SafetyPage;
